import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Login from './components/Login';
import FactoryAttendance from './components/FactoryAttendance';
import SupervisorDashboard from './components/SupervisorDashboard';
import SupervisorDevices from './components/SupervisorDevices';
import OwnerConfiguration from './components/OwnerConfiguration';
import OwnerEmployees from './components/OwnerEmployees';
import OwnerSupervision from './components/OwnerSupervision';
import DirectMessages from './components/DirectMessages';
import EmployeeProfile from './components/EmployeeProfile';
import { attendanceAPI, managementAPI } from './services/api';
import { Icons } from './components/Icons';
import HelpMenu from './components/HelpMenu';
import { formatAttendanceHm } from './utils/attendanceTime';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const DashboardRouter = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    if (user?.role === 'owner') return 'configuracion';
    if (user?.role === 'supervisor') return 'supervisor-dashboard';
    return 'fichada';
  });
  const [showProfile, setShowProfile] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(localStorage.getItem('selected_company_id') || '');
  const [companyLoading, setCompanyLoading] = useState(false);
  const [employeeFactories, setEmployeeFactories] = useState([]);
  const [employeeActivePunchHint, setEmployeeActivePunchHint] = useState(null);
  /** Al abrir Mensajes desde supervisión: `userId:factoryId` para seleccionar esa conversación */
  const [dmPendingConversationKey, setDmPendingConversationKey] = useState(null);

  const handleLogout = () => {
    logout();
  };

  const loadCompanies = async () => {
    if (user?.role !== 'owner') return;
    const res = await managementAPI.companies();
    const rows = res.data || [];
    setCompanies(rows);
    const current = selectedCompanyId;
    const exists = rows.some((c) => String(c.id) === String(current));
    const next = exists ? String(current) : (rows[0] ? String(rows[0].id) : '');
    setSelectedCompanyId(next);
    localStorage.setItem('selected_company_id', next);
  };

  useEffect(() => {
    (async () => {
      if (user?.role !== 'owner') return;
      try {
        setCompanyLoading(true);
        await loadCompanies();
      } finally {
        setCompanyLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const refreshEmployeeActivePunch = async () => {
      if (user?.role !== 'employee') {
        if (!cancelled) setEmployeeActivePunchHint(null);
        return;
      }
      try {
        const sessionRes = await attendanceAPI.punchActiveSession();
        const session = sessionRes.data || {};
        if (cancelled) return;
        if (!session.active) {
          setEmployeeActivePunchHint(null);
          return;
        }
        setEmployeeActivePunchHint({
          factoryId: session.factory_id,
          factoryName: session.factory_name,
          checkInTime: session.check_in_time || null,
        });
      } catch {
        if (!cancelled) setEmployeeActivePunchHint(null);
      }
    };

    refreshEmployeeActivePunch();
    if (user?.role === 'employee') {
      intervalId = window.setInterval(refreshEmployeeActivePunch, 30000);
    }
    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [user?.id, user?.role]);

  useEffect(() => {
    (async () => {
      if (user?.role !== 'employee') {
        setEmployeeFactories([]);
        return;
      }
      try {
        const res = await attendanceAPI.myFactories();
        setEmployeeFactories(res.data || []);
      } catch {
        setEmployeeFactories([]);
      }
    })();
  }, [user?.id, user?.role]);

  const isSupervisor = user?.role === 'supervisor';
  const isEmployee = user?.role === 'employee';

  useEffect(() => {
    if (isSupervisor && !['supervisor-dashboard', 'supervisor-devices', 'dm', 'profile'].includes(activeTab)) {
      setActiveTab('supervisor-dashboard');
    }
    if (isEmployee && ['supervisor-dashboard', 'supervisor-devices'].includes(activeTab)) {
      setActiveTab('fichada');
    }
  }, [activeTab, isSupervisor, isEmployee]);

  const handleQuickCreateCompany = async () => {
    const name = window.prompt('Nombre de la empresa');
    if (!name || !name.trim()) return;
    try {
      setCompanyLoading(true);
      const res = await managementAPI.createCompany({ name: name.trim() });
      const newId = String(res.data?.id || '');
      await loadCompanies();
      if (newId) {
        setSelectedCompanyId(newId);
        localStorage.setItem('selected_company_id', newId);
      }
      setShowProfile(false);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'No se pudo crear la empresa');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Mobile navigation items for different roles
  const getNavigationItems = () => {
    if (user?.role === 'owner') {
      return [
        { id: 'configuracion', label: 'Configuración', icon: Icons.Settings },
        { id: 'employees', label: 'Empleados', icon: Icons.Users },
        { id: 'supervision', label: 'Supervisión', icon: Icons.Shield },
        { id: 'dm', label: 'Mensajes', icon: Icons.MessageCircle },
      ];
    }
    if (user?.role === 'supervisor') {
      return [
        { id: 'supervisor-dashboard', label: 'Dashboard', icon: Icons.BarChart3 },
        { id: 'supervisor-devices', label: 'Dispositivos', icon: Icons.Smartphone },
        { id: 'dm', label: 'Mensajes', icon: Icons.MessageCircle },
        { id: 'profile', label: 'Perfil', icon: Icons.User },
      ];
    }
    return [
      { id: 'fichada', label: 'Fichada', icon: Icons.Building2 },
      { id: 'profile', label: 'Perfil', icon: Icons.User },
    ];
  };

  const navigationItems = getNavigationItems();
  const isOwner = user?.role === 'owner';
  const useEmployeeDesktopShell = user?.role === 'supervisor';

  const employeeDesktopHeader = useMemo(() => {
    switch (activeTab) {
      case 'fichada':
        return { title: 'Fichada', subtitle: 'Marcá entrada y salida en tu sede asignada.' };
      case 'supervisor-dashboard':
        return { title: 'Supervisión', subtitle: 'Vista del día del equipo: turnos, fichadas y estado.' };
      case 'supervisor-devices':
        return { title: 'Dispositivos', subtitle: 'Teléfonos y navegadores donde inició sesión tu equipo.' };
      case 'dm':
        return { title: 'Mensajes', subtitle: 'Conversaciones con tus contactos por sede.' };
      case 'profile':
        return { title: 'Perfil', subtitle: 'Tus datos, sedes y rol.' };
      default:
        return { title: 'SiCheck', subtitle: '' };
    }
  }, [activeTab]);

  const isDmView = (user?.role === 'employee' || user?.role === 'supervisor') && activeTab === 'dm';

  const renderMainContent = () => {
    if (user?.role === 'owner' && activeTab === 'configuracion') {
      return <OwnerConfiguration selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
    }
    if (user?.role === 'owner' && activeTab === 'employees') {
      return <OwnerEmployees selectedCompanyId={selectedCompanyId} />;
    }
    if (user?.role === 'owner' && activeTab === 'supervision') {
      return <OwnerSupervision selectedCompanyId={selectedCompanyId} />;
    }
    if (activeTab === 'dm') {
      return (
        <DirectMessages
          pendingConversationKey={dmPendingConversationKey}
          onPendingConversationConsumed={() => setDmPendingConversationKey(null)}
          selectedCompanyId={selectedCompanyId}
        />
      );
    }
    if ((user?.role === 'employee' || user?.role === 'supervisor') && activeTab === 'profile') {
      return <EmployeeProfile />;
    }
    if (user?.role === 'supervisor' && activeTab === 'supervisor-dashboard') {
      return (
        <SupervisorDashboard
          onOpenMessagesWithEmployee={(employeeUserId, factoryId) => {
            setDmPendingConversationKey(`${Number(employeeUserId)}:${Number(factoryId)}`);
            setActiveTab('dm');
          }}
        />
      );
    }
    if (user?.role === 'supervisor' && activeTab === 'supervisor-devices') {
      return <SupervisorDevices />;
    }
    if (activeTab === 'fichada' && user?.role === 'employee') {
      return <FactoryAttendance selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
    }
    if (user?.role === 'owner') {
      return <OwnerConfiguration selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
    }
    if (user?.role === 'supervisor') {
      return <SupervisorDashboard />;
    }
    return <FactoryAttendance selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
  };

  return (
    <div
      className={`app-container${isDmView ? ' app-container--dm' : ''}${isOwner ? ' app-container--owner' : ''}${
        useEmployeeDesktopShell ? ' app-container--employee-desktop' : ''
      }`}
    >
      {isOwner ? (
        <>
          {/* Owner mobile shell */}
          <div className="owner-mobile-shell">
            <header className="mobile-header">
              <div className="flex items-center gap-2">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-8 w-8 flex-shrink-0 rounded-lg"
                  width={32}
                  height={32}
                />
                <h1>SiCheck</h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
                >
                  👤
                </button>
              </div>
            </header>
            <main className="content-with-nav container-mobile">
              {renderMainContent()}
            </main>
            <nav className="bottom-nav">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <item.icon size={20} />
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Owner desktop shell */}
          <div className="owner-desktop-shell">
            <aside className="owner-desktop-sidebar">
              <div className="owner-desktop-brand">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-9 w-9 rounded-xl"
                  width={36}
                  height={36}
                />
                <div>
                  <p className="owner-desktop-brand-title">SiCheck</p>
                </div>
              </div>
              <div className="owner-desktop-nav">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`owner-desktop-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </aside>
            <div className="owner-desktop-main">
              <header className="owner-desktop-header">
                <div>
                  <h2 className="owner-desktop-title">
                    {activeTab === 'employees'
                      ? 'Empleados'
                      : activeTab === 'supervision'
                        ? 'Supervisión'
                        : activeTab === 'dm'
                          ? 'Mensajes'
                          : 'Configuración'}
                  </h2>
                  <p className="owner-desktop-subtitle">
                    {activeTab === 'supervision'
                      ? 'Relaciones supervisor–empleado por sede.'
                      : activeTab === 'dm'
                        ? 'Comunicados masivos por sede a empleados activos.'
                        : activeTab === 'employees'
                          ? 'Gestión de equipo, contratos y sedes.'
                          : 'Administración centralizada de empresas y sedes.'}
                  </p>
                </div>
                <div className="owner-desktop-header-actions">
                  <select
                    className="input-mobile py-1 px-2 text-sm min-h-0 h-10 min-w-[16rem]"
                    value={selectedCompanyId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedCompanyId(v);
                      localStorage.setItem('selected_company_id', v);
                    }}
                    disabled={companyLoading}
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-mobile btn-secondary text-lg font-semibold px-3 min-h-0 h-10"
                    onClick={handleQuickCreateCompany}
                    disabled={companyLoading}
                    title="Crear empresa rápida"
                  >
                    +
                  </button>
                  <button
                    onClick={() => setShowProfile(!showProfile)}
                    className="owner-desktop-profile-btn"
                    title="Abrir cuenta"
                  >
                    👤
                  </button>
                </div>
              </header>
              <main className="owner-desktop-content">
                {renderMainContent()}
              </main>
            </div>
          </div>
        </>
      ) : useEmployeeDesktopShell ? (
        <>
          <div className="employee-mobile-shell">
            <header className="mobile-header">
              <div className="flex items-center gap-2">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-8 w-8 flex-shrink-0 rounded-lg"
                  width={32}
                  height={32}
                />
                <h1>SiCheck</h1>
              </div>
              <div className="flex items-center gap-2">
                <HelpMenu />
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
                >
                  👤
                </button>
              </div>
            </header>
            <main className="content-with-nav container-mobile">
              {renderMainContent()}
            </main>
            <nav className="bottom-nav">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <item.icon size={20} />
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="employee-desktop-shell">
            <aside className="owner-desktop-sidebar">
              <div className="owner-desktop-brand">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-9 w-9 rounded-xl"
                  width={36}
                  height={36}
                />
                <div>
                  <p className="owner-desktop-brand-title">SiCheck</p>
                  <p className="owner-desktop-brand-sub">Supervisión y fichada</p>
                </div>
              </div>
              <div className="owner-desktop-nav">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    className={`owner-desktop-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </aside>
            <div className="owner-desktop-main">
              <header className="owner-desktop-header">
                <div>
                  <h2 className="owner-desktop-title">{employeeDesktopHeader.title}</h2>
                  <p className="owner-desktop-subtitle">{employeeDesktopHeader.subtitle}</p>
                </div>
                <div className="owner-desktop-header-actions">
                  <HelpMenu />
                  <button
                    type="button"
                    onClick={() => setShowProfile(!showProfile)}
                    className="owner-desktop-profile-btn"
                    title="Abrir cuenta"
                  >
                    👤
                  </button>
                </div>
              </header>
              <main className="owner-desktop-content">{renderMainContent()}</main>
            </div>
          </div>
        </>
      ) : (
        <>
          <header className="mobile-header">
            <div className="flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                alt=""
                className="h-8 w-8 flex-shrink-0 rounded-lg"
                width={32}
                height={32}
              />
              <h1>SiCheck</h1>
            </div>
            <div className="flex items-center gap-2">
              <HelpMenu />
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
              >
                👤
              </button>
            </div>
          </header>
          <main className="content-with-nav container-mobile">
            {renderMainContent()}
          </main>
          <nav className="bottom-nav">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
              >
                <item.icon size={20} />
                <span className="text-xs">{item.label}</span>
              </button>
            ))}
          </nav>
        </>
      )}

      {/* Profile Dropdown */}
      {showProfile && (
        <div className="modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Cuenta</h3>
              <button onClick={() => setShowProfile(false)} className="text-2xl">×</button>
            </div>
            <div className="modal-content">
              <div className="space-y-4">
                {user?.role === 'owner' && (
                  <div className="space-y-2">
                    <label className="form-label-mobile">Empresa activa</label>
                    <div className="flex items-center gap-2">
                      <select
                        className="input-mobile py-1 px-2 text-sm min-h-0 h-10 flex-1"
                        value={selectedCompanyId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedCompanyId(v);
                          localStorage.setItem('selected_company_id', v);
                          setShowProfile(false);
                        }}
                        disabled={companyLoading}
                      >
                        <option value="">Seleccionar empresa...</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-mobile btn-secondary text-lg font-semibold px-3 min-h-0 h-10"
                        onClick={handleQuickCreateCompany}
                        disabled={companyLoading}
                        title="Crear empresa rápida"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="form-label-mobile">Nombre</label>
                  <p className="text-lg font-medium">{user?.name}</p>
                </div>
                <div>
                  <label className="form-label-mobile">Email</label>
                  <p className="text-gray-600 break-all">{user?.email}</p>
                </div>
                <div>
                  <label className="form-label-mobile">Perfil</label>
                  <p className="text-gray-600 capitalize">{user?.role === 'owner' ? 'Dueño' : 'Empleado'}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="btn-mobile btn-danger w-full mt-6"
                >
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {user?.role === 'employee' && employeeActivePunchHint && activeTab !== 'fichada' && (
        <button
          type="button"
          className="sm:hidden fixed z-[65] right-4 bottom-24 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg text-left max-w-[calc(100vw-2rem)]"
          onClick={() => {
            localStorage.setItem('preferred_checkout_factory_id', String(employeeActivePunchHint.factoryId));
            setActiveTab('fichada');
          }}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Fichada activa</p>
          <p className="text-sm font-semibold text-amber-900 mt-0.5">{employeeActivePunchHint.factoryName}</p>
          <p className="text-xs text-amber-700 mt-1">
            {employeeActivePunchHint.checkInTime
              ? `Ingresaste ${formatAttendanceHm(employeeActivePunchHint.checkInTime)}. `
              : ''}
            Tocar para checkout rapido
          </p>
        </button>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <div className="App app-shell">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                  <DashboardRouter />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Navigate to="/dashboard" />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;